import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import { AppEventType } from '@core/events';
import { history } from '@core/history';
import { AccountErrorCode } from '@gqlTypes/globalTypes';
import { loginUrl } from '@urls';
import { setBrowserCredentials, webCredentialsAPI } from '@utils/misc';

import { AuthContext, IAuthContext, ILogin } from './context';
import { useTokenAuthMutation } from './mutations.rest';
import { useUserQuery } from './queries.rest';
import { getAuthToken, removeAuthToken, setAuthToken } from './utils';

type IState = Pick<IAuthContext, 'token' | 'user'> & { loading: boolean };

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const cachedToken = getAuthToken();
  const [context, setContext] = useState<IState>({
    loading: !!cachedToken,
    token: cachedToken,
    user: null,
  });
  const [tokenAuthMutation] = useTokenAuthMutation();
  const { refetch } = useUserQuery();
  const isLoggedIn = !!context.user && !!context.token;
  const queryClient = useQueryClient();

  const login: ILogin = async (loginData) => {
    if (!context.loading) {
      setContext((p) => ({ ...p, loading: true }));
    }

    try {
      const {
        data: {
          tokenCreate: { errors, user, token },
        },
      } = await tokenAuthMutation({ variables: loginData });

      if (errors.length) {
        setContext((p) => ({ ...p, loading: false }));
        return { errors, user: null };
      }

      setAuthToken(token!);
      setBrowserCredentials(loginData);
      // Clear React Query cache so previous user's data is not shown after login
      queryClient.clear();
      try {
        const { data } = await refetch();
        const meUser = data?.me ?? user;
        setContext((p) => ({ ...p, loading: false, token, user: meUser }));
        return { errors, user: meUser };
      } catch {
        // Fallback to login response user if /me fails unexpectedly
        setContext((p) => ({ ...p, loading: false, token, user }));
        return { errors, user };
      }
    } catch {
      setContext((p) => ({ ...p, loading: false }));
      return {
        errors: [
          {
            __typename: 'AccountError' as const,
            code: AccountErrorCode.INVALID,
            message: 'Wystąpił nieoczekiwany błąd.',
            field: null,
          },
        ],
        user: null,
      };
    }
  };

  const loginByToken = async (token: string) => {
    setContext((p) => ({ ...p, loading: true }));
    setAuthToken(token);
    try {
      const { data } = await refetch();
      setContext((p) => ({
        ...p,
        token,
        loading: false,
        user: data?.me,
      }));
    } catch {
      removeAuthToken();
      setContext((p) => ({
        ...p,
        loading: false,
        token: null,
        user: null,
      }));
    }
  };

  const logout = () => {
    removeAuthToken();
    // Clear React Query cache so this user's data is not visible after logout
    queryClient.clear();
    setContext((p) => ({ ...p, token: null, user: null }));

    if (webCredentialsAPI()) {
      navigator.credentials.preventSilentAccess();
    }
  };
  const redirectToLogin = () => {
    if (!isLoggedIn) {
      history.push(loginUrl);
    }
  };

  useEffect(() => {
    addEventListener(AppEventType.AuthError, redirectToLogin);
    return () => removeEventListener(AppEventType.AuthError, redirectToLogin);
  }, [context]);

  useEffect(() => {
    if (cachedToken) {
      (async () => {
        try {
          const { data } = await refetch();
          setContext((p) => ({
            ...p,
            loading: false,
            user: data?.me,
          }));
        } catch {
          // 401 or other error - treat as not logged in, don’t break the page
          removeAuthToken();
          setContext((p) => ({
            ...p,
            loading: false,
            token: null,
            user: null,
          }));
        }
      })();
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...context,
        isLoggedIn,
        login,
        loginByToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.displayName = 'AuthProvider';
